import { ref, Ref } from 'vue'
import type { JobFilterValue, MultiselectOption } from '@ironsight/types'
import { useActivityFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useActivityFilter'
import { useTagsFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useTagsFilter'
import { useResourceTypeJobFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useResourceTypeJobFilter'
import { useResourceFilter } from '@/components/jobs/job-search-page/filter-fields/data-providers/useResourceFilter'
import { priorities } from '@/utils/constants'
import { LOCATION_FILTER_IDS } from '@/components/jobs/job-search-page/useFilterChips'

export const isLocationFilterProperty = (property: string) => LOCATION_FILTER_IDS.includes(property)
const isPlainValueFilterProperty = (property: string) =>
  LOCATION_FILTER_IDS.includes(property) || property === 'Priority'

export const createAutocompleteFilterValue = (
  filterProperty: string,
  displayValue: string,
  resolvedValue: string | number | null,
) =>
  isPlainValueFilterProperty(filterProperty)
    ? [resolvedValue]
    : [{ label: displayValue, value: resolvedValue }]

export const appendAutocompleteFilterValue = (
  filterProperty: string,
  currentValue: JobFilterValue[],
  displayValue: string,
  resolvedValue: string | number | null,
) => {
  if (isPlainValueFilterProperty(filterProperty)) {
    const plainValues = currentValue as Array<string | number | null>
    const alreadyHas = plainValues.some((value) => String(value) === String(resolvedValue))

    return {
      nextValue: alreadyHas ? plainValues : [...plainValues, resolvedValue],
      alreadyHas,
    }
  }

  const alreadyHas = currentValue.some(
    (value) =>
      typeof value === 'object' &&
      value !== null &&
      'value' in value &&
      String((value as any).value) === String(resolvedValue),
  )

  return {
    nextValue: alreadyHas
      ? currentValue
      : [...currentValue, { label: displayValue, value: resolvedValue }],
    alreadyHas,
  }
}

export function useAutocompleteFilterAdapter() {
  const { options: activityOptions } = useActivityFilter()
  const { options: tagsOptions } = useTagsFilter()
  const { options: resourceTypeOptions } = useResourceTypeJobFilter()
  const { options: resourceOptions } = useResourceFilter()
  const priorityOptions = ref<MultiselectOption[]>(
    priorities.map((p) => ({ label: p.name, value: p.id })),
  )

  const optionsByProperty: Record<string, Ref<MultiselectOption[]>> = {
    TaskId: activityOptions,
    Tags: tagsOptions,
    ResourceTypeId: resourceTypeOptions,
    ResourceId: resourceOptions,
    Priority: priorityOptions,
  }

  function resolve(
    filterProperty: string,
    rawValueId: string,
    displayValue?: string,
  ): string | number | null {
    if (isLocationFilterProperty(filterProperty)) {
      if (rawValueId === '' || rawValueId === 'null') {
        return null
      }

      const parsedValue = Number(rawValueId)
      return Number.isNaN(parsedValue) ? rawValueId : parsedValue
    }

    if (filterProperty === 'CostCenter') {
      return displayValue ?? rawValueId
    }

    const options = optionsByProperty[filterProperty]
    if (!options) return rawValueId

    const match = options.value.find((opt) => String(opt.value).split(',').includes(rawValueId))
    return match ? match.value : rawValueId
  }

  return {
    resolve,
    createFilterValue: createAutocompleteFilterValue,
    appendFilterValue: appendAutocompleteFilterValue,
  }
}
